import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "../components/sections/Footer";
import { SITE } from "../lib/site";

const COMPANY = {
  name: "Adrien Sebert – DJ AS Animation",
  status: "Entreprise individuelle",
  siret: "532 346 673 00017",
  ape: "9329Z – Autres activités récréatives et de loisirs",
  address: "8 Rue Jean Montreuil, 61500 La Chapelle-près-Sées, France",
  host: "À compléter (nom, adresse et téléphone de l'hébergeur du site)",
};

const MENTIONS = {
  title: "Mentions légales",
  intro: "Conformément aux dispositions de la loi n° 2004-575 du 21 juin 2004 pour la confiance dans l'économie numérique, voici les informations relatives à l'éditeur et à l'hébergeur de ce site.",
  blocks: [
    { h: "Éditeur du site", p: [
      `${COMPANY.name}`, `${COMPANY.status}`, `SIRET : ${COMPANY.siret}`, `Code APE : ${COMPANY.ape}`,
      `Adresse : ${COMPANY.address}`, `Téléphone : ${SITE.phoneDisplay}`, `E-mail : ${SITE.email}`,
    ] },
    { h: "Directeur de la publication", p: ["Adrien Sebert"] },
    { h: "Hébergement", p: [COMPANY.host] },
    { h: "Propriété intellectuelle", p: [
      "L'ensemble des contenus de ce site (textes, photographies, vidéos, logos, éléments graphiques, musiques) est la propriété exclusive d'Adrien Sebert ou de ses partenaires, et est protégé par le Code de la propriété intellectuelle. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable est interdite.",
    ] },
    { h: "Crédits", p: ["Photographies et vidéos : Adrien Sebert. Marques et logos tiers (Spotify, YouTube, TikTok, Instagram, Facebook, Google) appartiennent à leurs propriétaires respectifs."] },
    { h: "Responsabilité", p: [
      "L'éditeur s'efforce de fournir des informations exactes et à jour, sans toutefois pouvoir en garantir l'exhaustivité. Il ne saurait être tenu responsable des dommages directs ou indirects résultant de l'accès ou de l'utilisation du site, ni du contenu des sites tiers accessibles par lien hypertexte.",
    ] },
    { h: "Droit applicable", p: ["Le présent site est soumis au droit français. Tout litige relève de la compétence des tribunaux français."] },
  ],
};

const PRIVACY = {
  title: "Politique de confidentialité",
  intro: "Cette politique décrit la manière dont vos données personnelles sont collectées et traitées lorsque vous utilisez ce site, conformément au Règlement général sur la protection des données (RGPD) et à la loi Informatique et Libertés.",
  blocks: [
    { h: "Responsable du traitement", p: [`${COMPANY.name} – ${COMPANY.address} – ${SITE.email}`] },
    { h: "Données collectées", p: [
      "Formulaire de contact / demande de devis : nom, e-mail, téléphone, type d'événement, date, lieu, nombre d'invités, budget indicatif et message.",
      "Statistiques de fréquentation : page consultée, source de trafic, type d'appareil et navigateur. Ces données sont anonymes et ne permettent pas de vous identifier. Aucun cookie publicitaire ni traceur tiers n'est déposé.",
    ] },
    { h: "Finalités et base légale", p: [
      "Répondre à vos demandes de renseignements et établir un devis (exécution de mesures précontractuelles et consentement).",
      "Mesurer l'audience du site afin d'en améliorer le contenu (intérêt légitime).",
    ] },
    { h: "Durée de conservation", p: ["Les demandes de contact sont conservées 3 ans à compter du dernier échange. Les statistiques anonymes sont conservées 25 mois."] },
    { h: "Destinataires", p: ["Vos données sont destinées exclusivement à Adrien Sebert. Elles ne sont ni vendues ni cédées à des tiers. Elles sont hébergées sur des serveurs sécurisés situés dans l'Union européenne ou chez des prestataires offrant des garanties équivalentes."] },
    { h: "Contenus tiers", p: ["Ce site intègre des lecteurs YouTube et Spotify. Lors de leur affichage ou lecture, ces services peuvent collecter des données selon leurs propres politiques de confidentialité."] },
    { h: "Vos droits", p: [
      "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données. Vous pouvez l'exercer à tout moment en écrivant à " + SITE.email + ".",
      "Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la CNIL (www.cnil.fr).",
    ] },
  ],
};

export default function LegalPage({ kind }) {
  const doc = kind === "privacy" ? PRIVACY : MENTIONS;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${doc.title} – ${SITE.brand}`;
  }, [doc.title]);

  return (
    <div className="App relative bg-black min-h-screen">
      <header className="px-6 md:px-10 lg:px-16 py-5 border-b border-white/10 flex items-center justify-between">
        <Link to="/" data-testid="legal-back-home" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-[#D4AF37] transition-colors">
          <ArrowLeft size={16} /> Retour au site
        </Link>
        <Link to="/" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E9C766] via-[#D4AF37] to-[#8a6618] flex items-center justify-center text-black font-bold text-sm">AS</span>
          <span className="text-white font-semibold text-sm hidden sm:inline">DJ AS Animation</span>
        </Link>
      </header>

      <main data-testid={`legal-page-${kind}`} className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <span className="eyebrow">Informations légales</span>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight gold-text-gradient">{doc.title}</h1>
          <p className="mt-6 text-zinc-400 leading-relaxed">{doc.intro}</p>

          <div className="mt-12 space-y-10">
            {doc.blocks.map((b) => (
              <section key={b.h}>
                <h2 className="text-base md:text-lg font-semibold text-white">{b.h}</h2>
                <div className="mt-3 space-y-2">
                  {b.p.map((t) => (
                    <p key={t} className="text-sm md:text-base text-zinc-300 leading-relaxed">{t}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap gap-4 text-sm">
            <Link to="/mentions-legales" data-testid="legal-link-mentions" className={`hover:text-[#D4AF37] ${kind === "mentions" ? "text-[#D4AF37]" : "text-zinc-400"}`}>Mentions légales</Link>
            <Link to="/confidentialite" data-testid="legal-link-privacy" className={`hover:text-[#D4AF37] ${kind === "privacy" ? "text-[#D4AF37]" : "text-zinc-400"}`}>Politique de confidentialité</Link>
          </div>
          <p className="mt-4 text-xs text-zinc-600">Dernière mise à jour : septembre 2026</p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
